/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest(".js-product");
      this.productForm = this.section.querySelector(".js-product-form");
      this.optionSelectors = this.querySelectorAll(".option-selector");
      this.data = this.getProductData();

      // Ensure we have an initial variant reference for UI toggles.
      const selectedOptions = this.getSelectedOptions();
      this.variant = this.data.product.variants.find((v) => v.options.every((val, index) => val === selectedOptions[index]));

      this.updateAvailability();
      this.addEventListener("change", this.handleVariantChange.bind(this));
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      const selectedOptions = this.getSelectedOptions();
      this.variant = null;

      // Get selected variant data (if variant exists).
      this.variant = this.data.product.variants.find((v) => v.options.every((val, index) => val === selectedOptions[index]));

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAvailability();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateFlooring();
      this.updateCarpet();
      VariantPicker.updateLabelText(evt);

      // Dispatch event before button update so sokkel-variant-toggle.js can handle button text
      this.dispatchEvent(
        new CustomEvent("on:variant:change", {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product,
          },
        })
      );

      // Update gulv calculator after sokkel toggle has had a chance to hide/show elements
      setTimeout(() => {
        this.updateGulvCalculator();
      }, 0);

      // Update button after event dispatch so sokkel-variant-toggle.js can override text
      this.updateAddToCartButton();
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector(".js-product-form");
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;

      // For gulvprodukt, let sokkel-variant-toggle.js handle button text via event
      const hasGulvCalculator = this.section.querySelector("[data-gulv-calculator]") || this.section.querySelector("[data-qty-gulv]");

      if (!hasGulvCalculator) {
        // Non-gulvprodukt: always update button text
        this.addBtn.textContent = variantAvailable ? this.addBtn.dataset.addToCartText : unavailableStr;
      } else if (!variantAvailable) {
        // Gulvprodukt but sold out: show sold out message
        this.addBtn.textContent = unavailableStr;
      }
      // For available gulvprodukt variants, sokkel-variant-toggle.js handles the button text
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === "false") return;
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.getSelectedOptions() };
      }

      const updateOptionAvailability = (optionEl, available, soldout) => {
        const el = optionEl;
        const text = soldout ? theme.strings.noStock : theme.strings.noVariant;
        el.classList.toggle("is-unavailable", !available);

        if (optionEl.classList.contains("custom-select__option")) {
          const em = el.querySelector("em");

          if (em) {
            em.hidden = available;
          }

          if (!available) {
            if (em) {
              em.textContent = text;
            } else {
              el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${text}</em>`;
            }
          }
        } else if (!available) {
          el.nextElementSibling.title = text;
        } else {
          el.nextElementSibling.removeAttribute("title");
        }
      };

      // Flag all options as unavailable
      this.querySelectorAll(".js-option").forEach((optionEl) => {
        updateOptionAvailability(optionEl, false, false);
      });

      // Flag selector options as available or sold out, depending on the variant availability
      this.optionSelectors.forEach((selector, selectorIndex) => {
        this.data.product.variants.forEach((variant) => {
          let matchCount = 0;

          variant.options.forEach((option, optionIndex) => {
            if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
              matchCount += 1;
            }
          });

          if (matchCount === currVariant.options.length - 1) {
            const options = selector.querySelectorAll(".js-option");
            const optionEl = Array.from(options).find((opt) => {
              if (selector.dataset.selectorType === "dropdown") {
                return opt.firstElementChild.textContent === variant.options[selectorIndex];
              }
              return opt.nextElementSibling.querySelector(".js-value").textContent === variant.options[selectorIndex];
            });

            if (optionEl) {
              updateOptionAvailability(optionEl, variant.available, !variant.available);
            }
          }
        });
      });
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector(".backorder");
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === "none") {
          const backorderProdEl = this.backorder.querySelector(".backorder__product");
          const prodTitleEl = this.section.querySelector(".product-title");
          const variantTitle = this.variant.title.includes("Default") ? "" : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest(".option-selector");
      if (selector.dataset.selectorType === "dropdown") return;

      const colorText = selector.querySelector(".js-color-text");
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector(".js-value").textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches("quick-add-drawer")) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector("media-gallery");
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(`[data-media-id="${this.variant.featured_media.id}"]`);
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector("pickup-availability");
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute("available");
        this.pickUpAvailability.innerHTML = "";
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector(".product-info__price > .price");
      if (!this.price) return;

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector(".price__current");
        const priceWasEl = this.price.querySelector(".price__was");
        const unitPriceEl = this.price.querySelector(".unit-price");
        var dataforjs = document.querySelector("#dataforjs");

        // Update current price and original price if on sale.
        priceCurrentEl.textContent = this.data.formatted[this.variant.id].price;
        if (priceWasEl) priceWasEl.textContent = this.data.formatted[this.variant.id].compareAtPrice || "";

        if (dataforjs) {
          let priceNew = parseFloat(priceCurrentEl.textContent.replace(",", ".")) * 10;
          priceCurrentEl.textContent = priceNew.toFixed(2).replace(".", ",") + " kr";
          let priceWasNew = parseFloat(priceWasEl.textContent.replace(",", ".")) * 10;
          priceWasEl.textContent = priceWasNew.toFixed(2).replace(".", ",") + " kr";
        }

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector(".unit-price__price");
          const unitEl = this.price.querySelector(".unit-price__unit");
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.textContent = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle("price--on-sale", this.variant.compare_at_price > this.variant.price);
        this.price.classList.toggle("price--sold-out", !this.variant.available);
      }

      this.price.querySelector(".price__default").hidden = !this.variant;
      this.price.querySelector(".price__no-variant").hidden = this.variant;
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector(".product-sku__value");
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : "";
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the gulv (floor) calculator when variant changes.
     */
    updateGulvCalculator() {
      var calculator = this.section.querySelector("[data-gulv-calculator]");

      // Don't update if calculator is hidden (e.g., sokkel variant)
      if (!calculator || calculator.hidden) {
        return;
      }

      var totalAmountFloor = this.section.querySelector(".qty-input__input.input.gulvinput");
      var sqmFloor = calculator.querySelector("#sqm-floor");
      var packageAmount = calculator.querySelector("#package-amount");
      var packageTotal = calculator.querySelector("#total-amount-of-packages");
      var pricePackageOutput = calculator.querySelector("#price-package");
      var amountSqm = calculator.querySelector("#total-sqm");
      var amountPackages = calculator.querySelector("#amountpackages");

      // Only run if calculator elements exist and variant is available
      if (!totalAmountFloor || !sqmFloor || !packageAmount || !packageTotal || !pricePackageOutput || !amountSqm || !this.variant) {
        return;
      }

      // Validate input values
      var sqmValue = parseFloat(sqmFloor.value);
      var packageValue = parseFloat(packageAmount.value);

      if (isNaN(sqmValue) || isNaN(packageValue) || packageValue === 0) {
        console.warn("Invalid calculator values:", { sqmValue, packageValue });
        return;
      }

      var amountPackagesText = amountPackages ? amountPackages.innerText.replace(/r$/, "") : "Pakke";

      // Recalculate based on current sqm input
      var totalAmountCalc = sqmValue / packageValue;
      var totalAmountCalcRoundedUp = Math.ceil(totalAmountCalc);

      if (totalAmountCalcRoundedUp > 1 && amountPackages) {
        amountPackages.innerText = amountPackagesText + "r";
      } else if (amountPackages) {
        amountPackages.innerText = amountPackagesText;
      }

      totalAmountFloor.value = totalAmountCalcRoundedUp;
      packageTotal.innerHTML = totalAmountCalcRoundedUp;

      // Use raw variant price (in cents) for reliable calculation
      var packagePrice = this.variant.price / 100; // Convert from cents to currency
      var totalPriceCalc = totalAmountCalcRoundedUp * packagePrice;

      // Format price with Danish locale (thousand separator and decimal comma)
      var totalPriceFormatted = totalPriceCalc.toLocaleString("da-DK", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      pricePackageOutput.innerHTML = totalPriceFormatted + " kr";

      var amountSqmCalc = (packageValue * totalAmountCalcRoundedUp).toFixed(2);
      var amountSqmCalcWithComma = amountSqmCalc.replace(".", ",");
      amountSqm.innerHTML = amountSqmCalcWithComma + " m<sup>2</sup>";
    }

    updateCarpet() {
      var quantityPicker = document.querySelector(".qty-input__input.input.gulvtappe");
      var flooringAmount = document.getElementById("flooring");
      var totalFlooringAmount = document.getElementById("total-amonunt-of-flooring");
      var pricePrSqmFlooring = document.getElementById("backendprice");
      var priceFlooring = document.getElementById("price-flooring");
      var activeVariantPicker = document.querySelector('.option-selector__btns.flex.flex-wrap input[type="radio"]:checked');

      // Only run if carpet calculator elements exist
      if (!quantityPicker || !flooringAmount || !totalFlooringAmount || !pricePrSqmFlooring || !priceFlooring || !activeVariantPicker) {
        return;
      }

      var activeVariantPickerValue = parseFloat(activeVariantPicker.value);
      var sqmFlooring = Math.round((activeVariantPickerValue * flooringAmount.value) / 100) / 100;
      console.log(sqmFlooring);
      var sqmFlooringRounded = Math.ceil(sqmFlooring * 10) / 10;
      console.log(sqmFlooringRounded);
      if (sqmFlooring <= 0) {
        quantityPicker.value = 10;
        totalFlooringAmount.innerHTML = 1;
      } else {
        quantityPicker.value = sqmFlooringRounded * 10;
        totalFlooringAmount.innerHTML = sqmFlooringRounded;
      }
      var priceNoCurrency = pricePrSqmFlooring.textContent.replace(",", ".").replace(" kr", "");
      var totalFlooringPrice = (priceNoCurrency * sqmFlooringRounded).toFixed(2);
      var totalFlooringPriceCurrency = totalFlooringPrice.replace(".", ",");
      if (totalFlooringPrice <= 0) {
        priceFlooring.textContent = pricePrSqmFlooring.textContent;
      } else {
        priceFlooring.innerHTML = totalFlooringPriceCurrency + " kr";
      }
      var lineItemSqm = document.getElementById("lineitemsqm");
      if (lineItemSqm) {
        lineItemSqm.setAttribute("value", sqmFlooringRounded);
      }
    }

    updateFlooring() {
      var quantityPicker = document.querySelector(".qty-input__input.input.gulvtappe");
      var flooringAmount = document.getElementById("flooring");
      var totalFlooringAmount = document.getElementById("total-amonunt-of-flooring");
      var pricePrSqmFlooring = document.getElementById("backendprice");
      var priceFlooring = document.getElementById("price-flooring");
      var activeVariantPicker = document.querySelector('.option-selector__btns.flex.flex-wrap input[type="radio"]:checked');

      // Only run if flooring calculator elements exist
      if (!flooringAmount || !quantityPicker || !totalFlooringAmount || !pricePrSqmFlooring || !priceFlooring || !activeVariantPicker) {
        return;
      }

      var activeVariantPickerValue = parseFloat(activeVariantPicker.value);
      var sqmFlooring = ((activeVariantPickerValue * flooringAmount.value) / 10000).toFixed(0);

      if (sqmFlooring <= 0) {
        quantityPicker.value = 1;
        totalFlooringAmount.innerHTML = 1;
      } else {
        quantityPicker.value = sqmFlooring;
        totalFlooringAmount.innerHTML = sqmFlooring;
      }
      var priceNoCurrency = pricePrSqmFlooring.textContent.replace(",", ".").replace(" kr", "");
      var totalFlooringPrice = (priceNoCurrency * sqmFlooring).toFixed(2);
      var totalFlooringPriceCurrency = totalFlooringPrice.replace(".", ",");
      if (totalFlooringPrice <= 0) {
        priceFlooring.textContent = pricePrSqmFlooring.textContent;
      } else {
        priceFlooring.innerHTML = totalFlooringPriceCurrency + " kr";
      }
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== "change" || this.dataset.updateUrl === "false") return;
      window.history.replaceState({}, "", `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll(".js-product-form, .js-instalments-form");

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === "dropdown") {
          selectedOptions.push(selector.querySelector(".custom-select__btn").textContent.trim());
        } else {
          selectedOptions.push(selector.querySelector("input:checked").value);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }
  }

  customElements.define("variant-picker", VariantPicker);
}
